import { useState, useEffect } from 'react';
import api from '../lib/api';
import { getErrorMessage } from '../utils/errorUtils';
import { formatRut, formatDate, AID_TYPES } from '../lib/utils';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Truck,
  Plus,
  Search,
  Loader2,
  User,
  Package,
  Trash2,
  AlertCircle,
} from 'lucide-react';

const Deliveries = () => {
  const { user } = useAuth();
  const [deliveries, setDeliveries] = useState([]);
  const [beneficiaries, setBeneficiaries] = useState([]);
  const [products, setProducts] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Form state
  const [selectedBeneficiary, setSelectedBeneficiary] = useState(null);
  const [aidType, setAidType] = useState('');
  const [notes, setNotes] = useState('');
  const [deliveryItems, setDeliveryItems] = useState([]);
  const [currentItem, setCurrentItem] = useState({
    product_id: '',
    quantity: 1,
  });

  const fetchData = async () => {
    try {
      const [deliveriesRes, beneficiariesRes, productsRes, warehousesRes] = await Promise.all([
        api.get('/deliveries'),
        api.get('/beneficiaries'),
        api.get('/products'),
        api.get('/warehouses'),
      ]);
      setDeliveries(deliveriesRes.data);
      setBeneficiaries(beneficiariesRes.data);
      setProducts(productsRes.data);
      setWarehouses(warehousesRes.data);
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const filteredDeliveries = deliveries.filter(delivery => {
    if (!searchTerm) return true;
    return delivery.beneficiary_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
           delivery.beneficiary_rut.includes(searchTerm);
  });

  const handleBeneficiarySelect = (beneficiaryId) => {
    const ben = beneficiaries.find(b => b.beneficiary_id === beneficiaryId);
    setSelectedBeneficiary(ben);
  };

  const addItem = () => {
    if (!currentItem.product_id || currentItem.quantity < 1) {
      toast.error('Seleccione un producto y cantidad');
      return;
    }

    const product = products.find(p => p.product_id === currentItem.product_id);
    if (!product) return;

    if (currentItem.quantity > product.quantity) {
      toast.error(`Stock insuficiente. Disponible: ${product.quantity}`);
      return;
    }

    // Check if already added
    const existing = deliveryItems.find(i => i.product_id === currentItem.product_id);
    if (existing) {
      toast.error('Este producto ya está en la lista');
      return;
    }

    setDeliveryItems([...deliveryItems, {
      product_id: product.product_id,
      product_name: product.name,
      quantity: currentItem.quantity,
      warehouse_id: product.warehouse_id,
      available: product.quantity,
    }]);

    setCurrentItem({ product_id: '', quantity: 1 });
  };

  const removeItem = (productId) => {
    setDeliveryItems(deliveryItems.filter(i => i.product_id !== productId));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedBeneficiary) {
      toast.error('Seleccione un beneficiario');
      return;
    }

    if (!aidType) {
      toast.error('Seleccione el tipo de ayuda');
      return;
    }

    if (deliveryItems.length === 0) {
      toast.error('Agregue al menos un producto');
      return;
    }

    setSubmitting(true);
    try {
      const deliveryData = {
        beneficiary_id: selectedBeneficiary.beneficiary_id,
        beneficiary_name: selectedBeneficiary.name,
        beneficiary_rut: selectedBeneficiary.rut,
        items: deliveryItems.map(item => ({
          product_id: item.product_id,
          product_name: item.product_name,
          quantity: item.quantity,
          warehouse_id: item.warehouse_id,
        })),
        aid_type: aidType,
        notes: notes,
        delivered_by: user?.name || 'Sistema',
      };

      await api.post('/deliveries', deliveryData);
      toast.success('Entrega registrada exitosamente');
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Error al registrar entrega'));
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setSelectedBeneficiary(null);
    setAidType('');
    setNotes('');
    setDeliveryItems([]);
    setCurrentItem({ product_id: '', quantity: 1 });
  };

  const availableProducts = products.filter(p => p.quantity > 0);

  return (
    <div className="space-y-6" data-testid="deliveries-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 font-['Manrope']">
            Entregas
          </h1>
          <p className="text-slate-500 mt-1">
            Registro de entregas de ayudas a beneficiarios
          </p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-teal-600 hover:bg-teal-700 shadow-lg shadow-teal-600/20" data-testid="new-delivery-btn">
              <Plus className="w-4 h-4 mr-2" />
              Nueva Entrega
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-['Manrope']">Registrar Nueva Entrega</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-6 mt-4">
              {/* Beneficiario */}
              <div className="space-y-2">
                <Label>Beneficiario *</Label>
                <Select onValueChange={handleBeneficiarySelect}>
                  <SelectTrigger className="bg-slate-50" data-testid="beneficiary-select">
                    <SelectValue placeholder="Buscar beneficiario..." />
                  </SelectTrigger>
                  <SelectContent>
                    {beneficiaries.map(ben => (
                      <SelectItem key={ben.beneficiary_id} value={ben.beneficiary_id}>
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4 text-slate-400" />
                          <span>{ben.name}</span>
                          <span className="text-slate-400">({formatRut(ben.rut)})</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {selectedBeneficiary && (
                  <Card className="bg-slate-50 border-slate-200">
                    <CardContent className="p-4">
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <p className="text-slate-500">Nombre</p>
                          <p className="font-medium">{selectedBeneficiary.name}</p>
                        </div>
                        <div>
                          <p className="text-slate-500">RUT</p>
                          <p className="font-medium">{formatRut(selectedBeneficiary.rut)}</p>
                        </div>
                        <div>
                          <p className="text-slate-500">Dirección</p>
                          <p className="font-medium">{selectedBeneficiary.address}</p>
                        </div>
                        <div>
                          <p className="text-slate-500">Tramo RSH</p>
                          <p className="font-medium">{selectedBeneficiary.rsh_tramo || 'No registrado'}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>

              {/* Tipo de Ayuda */}
              <div className="space-y-2">
                <Label>Tipo de Ayuda *</Label>
                <Select value={aidType} onValueChange={setAidType}>
                  <SelectTrigger className="bg-slate-50" data-testid="aid-type-select">
                    <SelectValue placeholder="Seleccionar tipo de ayuda" />
                  </SelectTrigger>
                  <SelectContent>
                    {AID_TYPES.map(type => (
                      <SelectItem key={type} value={type}>{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Productos */}
              <div className="space-y-4">
                <Label>Productos a Entregar *</Label>
                <div className="flex gap-2">
                  <Select 
                    value={currentItem.product_id} 
                    onValueChange={(v) => setCurrentItem(prev => ({ ...prev, product_id: v }))}
                  >
                    <SelectTrigger className="flex-1 bg-slate-50" data-testid="product-select">
                      <SelectValue placeholder="Seleccionar producto" />
                    </SelectTrigger>
                    <SelectContent>
                      {availableProducts.map(prod => (
                        <SelectItem key={prod.product_id} value={prod.product_id}>
                          <div className="flex items-center justify-between w-full gap-4">
                            <span>{prod.name}</span>
                            <Badge variant="outline" className="ml-2">
                              Stock: {prod.quantity}
                            </Badge>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input
                    type="number"
                    min="1"
                    value={currentItem.quantity}
                    onChange={(e) => setCurrentItem(prev => ({ ...prev, quantity: parseInt(e.target.value) || 1 }))}
                    className="w-24 bg-slate-50"
                    data-testid="quantity-input"
                  />
                  <Button type="button" onClick={addItem} variant="outline" data-testid="add-item-btn">
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>

                {/* Items List */}
                {deliveryItems.length > 0 && (
                  <div className="border rounded-lg overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Producto</TableHead>
                          <TableHead>Cantidad</TableHead>
                          <TableHead className="text-right">Acción</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {deliveryItems.map((item) => (
                          <TableRow key={item.product_id}>
                            <TableCell className="font-medium">{item.product_name}</TableCell>
                            <TableCell>{item.quantity}</TableCell>
                            <TableCell className="text-right">
                              <Button 
                                type="button" 
                                variant="ghost" 
                                size="sm"
                                onClick={() => removeItem(item.product_id)}
                              >
                                <Trash2 className="w-4 h-4 text-red-500" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}

                {deliveryItems.length === 0 && (
                  <div className="flex items-center gap-2 p-4 bg-amber-50 rounded-lg text-amber-700">
                    <AlertCircle className="w-5 h-5" />
                    <span className="text-sm">Agregue al menos un producto para la entrega</span>
                  </div>
                )}
              </div>

              {/* Notas */}
              <div className="space-y-2">
                <Label>Observaciones</Label>
                <Textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Notas adicionales sobre la entrega..."
                  className="bg-slate-50"
                />
              </div>

              {/* Submit */}
              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button 
                  type="submit" 
                  className="bg-teal-600 hover:bg-teal-700"
                  disabled={submitting}
                  data-testid="submit-delivery-btn"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Registrando...
                    </>
                  ) : (
                    <>
                      <Truck className="w-4 h-4 mr-2" />
                      Registrar Entrega
                    </>
                  )}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-teal-50 text-teal-600">
              <Truck className="w-5 h-5" />
            </div>
            <div>
              <p className="text-xs text-slate-500">Total Entregas</p>
              <p className="text-2xl font-bold text-slate-900">{deliveries.length}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-blue-50 text-blue-600">
              <User className="w-5 h-5" />
            </div>
            <div>
              <p className="text-xs text-slate-500">Beneficiarios Atendidos</p>
              <p className="text-2xl font-bold text-slate-900">
                {new Set(deliveries.map(d => d.beneficiary_id)).size}
              </p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-amber-50 text-amber-600">
              <Package className="w-5 h-5" />
            </div>
            <div>
              <p className="text-xs text-slate-500">Productos Entregados</p>
              <p className="text-2xl font-bold text-slate-900">
                {deliveries.reduce((acc, d) => acc + d.items.reduce((a, i) => a + i.quantity, 0), 0)}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <Card>
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Buscar por nombre o RUT del beneficiario..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-slate-50"
              data-testid="search-deliveries"
            />
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
            </div>
          ) : filteredDeliveries.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-slate-400">
              <Truck className="w-12 h-12 mb-4" />
              <p className="text-lg font-medium">No hay entregas registradas</p>
              <p className="text-sm">Registre la primera entrega para comenzar</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Fecha</TableHead>
                    <TableHead>Beneficiario</TableHead>
                    <TableHead className="hidden md:table-cell">Tipo de Ayuda</TableHead>
                    <TableHead className="hidden lg:table-cell">Productos</TableHead>
                    <TableHead className="hidden lg:table-cell">Entregado por</TableHead>
                    <TableHead>Estado</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredDeliveries.map((delivery) => (
                    <TableRow key={delivery.delivery_id} className="table-row-hover">
                      <TableCell>{formatDate(delivery.created_at)}</TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium text-slate-900">{delivery.beneficiary_name}</p>
                          <p className="text-xs text-slate-500">{formatRut(delivery.beneficiary_rut)}</p>
                        </div>
                      </TableCell>
                      <TableCell className="hidden md:table-cell">{delivery.aid_type}</TableCell>
                      <TableCell className="hidden lg:table-cell">
                        <div className="flex flex-wrap gap-1">
                          {delivery.items.slice(0, 2).map((item, idx) => (
                            <Badge key={idx} variant="outline" className="text-xs">
                              {item.product_name} x{item.quantity}
                            </Badge>
                          ))}
                          {delivery.items.length > 2 && (
                            <Badge variant="outline" className="text-xs">
                              +{delivery.items.length - 2}
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="hidden lg:table-cell">{delivery.delivered_by}</TableCell>
                      <TableCell>
                        <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">
                          Completada
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Deliveries;
